import React, { useState, useEffect } from 'react';
import { DollarSign, Package, Send, Users, Loader2, CreditCard, ShoppingCart, Gift, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { trialsAPI } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL !== undefined ? process.env.REACT_APP_BACKEND_URL : 'http://localhost:8001';

export const TrialAdminManager = ({ adminId, centerUsers }) => {
  const [packs, setPacks] = useState([]);
  const [centerPacks, setCenterPacks] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDistributing, setIsDistributing] = useState(false);
  const [isAssigningPack, setIsAssigningPack] = useState(false);
  const [distributionData, setDistributionData] = useState({ studentId: '', amount: 10 });
  const [packAssignData, setPackAssignData] = useState({ userId: '', packId: '' });
  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();
  const [paypalConfig, setPaypalConfig] = useState(null);

  useEffect(() => {
    loadData();
    loadPaypalConfig();
  }, [adminId]);

  const loadPaypalConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/site`);
      if (res.data.paypal_client_id) {
        setPaypalConfig(res.data);
        paypalDispatch({
          type: "resetOptions",
          value: {
            "client-id": res.data.paypal_client_id,
            currency: "USD",
            intent: "capture",
          },
        });
      }
    } catch (error) {
      console.error("Error loading paypal config:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [packsRes, balanceRes] = await Promise.all([
        axios.get(`${API_URL}/api/trials/packs`),
        axios.get(`${API_URL}/api/trials/balance/${adminId}`)
      ]);
      setPacks(packsRes.data);
      setBalance(balanceRes.data.essais_restants);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
    // Load center packs separately — non-blocking, failure won't affect the main UI
    try {
      const cpRes = await axios.get(`${API_URL}/api/settings/center-packs?admin_id=${adminId}`);
      setCenterPacks((cpRes.data || []).filter(p => (p.essais_ia || 0) > 0));
    } catch {
      // Center packs unavailable — assign-pack section will show empty state
    }
  };

  const handleAssignPack = async (e) => {
    e.preventDefault();
    if (!packAssignData.userId) return toast.error('Sélectionnez un étudiant');
    if (!packAssignData.packId) return toast.error('Sélectionnez un pack');
    const pack = centerPacks.find(p => p.id === packAssignData.packId);
    if (pack && (pack.essais_ia || 0) > balance) {
      return toast.error(`Solde insuffisant — ce pack requiert ${pack.essais_ia} essais`);
    }
    setIsAssigningPack(true);
    try {
      const res = await trialsAPI.distributePack(adminId, packAssignData.userId, packAssignData.packId);
      if (res.success) {
        toast.success(res.message);
        loadData();
        setPackAssignData({ userId: '', packId: '' });
      } else {
        toast.error(res.message || 'Erreur lors de l\'attribution');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'attribution');
    } finally {
      setIsAssigningPack(false);
    }
  };

  const handleDistribute = async (e) => {
    e.preventDefault();
    if (!distributionData.studentId) return toast.error('Sélectionnez un étudiant');
    
    setIsDistributing(true);
    try {
      const res = await axios.post(`${API_URL}/api/trials/distribute`, null, {
        params: {
          admin_id: adminId,
          student_id: distributionData.studentId,
          amount: distributionData.amount
        }
      });
      if (res.data.success) {
        toast.success(res.data.message);
        loadData();
        setDistributionData({ ...distributionData, studentId: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de distribution');
    } finally {
      setIsDistributing(false);
    }
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin h-8 w-8 text-primary" /></div>;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-primary to-blue-700 rounded-[2rem] p-10 text-white shadow-2xl shadow-primary/20 flex flex-col md:flex-row justify-between items-center relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12 translate-x-4 -translate-y-4">
          <CreditCard className="h-48 w-48" />
        </div>
        <div className="relative z-10 space-y-4 text-center md:text-left">
          <div className="inline-flex items-center space-x-2 bg-white/10 px-3 py-1 rounded-full backdrop-blur-md">
            <div className="h-2 w-2 rounded-full bg-white animate-pulse" />
            <p className="text-white font-bold uppercase tracking-widest text-[10px]">Votre Solde Actuel</p>
          </div>
          <h2 className="text-6xl font-black tracking-tighter">{balance} <span className="text-2xl font-normal opacity-80 tracking-normal">Essais IA</span></h2>
          <p className="text-blue-100 text-sm font-medium">Vous pouvez redistribuer ces essais à vos étudiants à tout moment.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Buy Packs Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ShoppingCart className="h-5 w-5 text-primary" />
              </div>
              <span>Acheter des Essais</span>
            </h3>
          </div>

          {!paypalConfig?.paypal_client_id && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 p-4 rounded-2xl text-yellow-500 text-sm">
              <p>Le paiement PayPal n'est pas encore configuré par le Super Admin.</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {packs.map(pack => (
              <div key={pack.id} className="group bg-card border border-border rounded-3xl p-5 space-y-5 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-xl relative overflow-hidden flex flex-col">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h4 className="font-bold text-foreground text-lg leading-tight">{pack.name}</h4>
                    <p className="text-3xl font-black text-primary tracking-tighter">{pack.essais_count} <span className="text-xs font-medium text-muted-foreground tracking-normal">Essais</span></p>
                  </div>
                  <div className="bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20">
                    <span className="text-green-500 font-black text-sm">${pack.price}</span>
                  </div>
                </div>
                
                <div className="mt-auto pt-4">
                  {paypalConfig?.paypal_client_id ? (
                    <PayPalButtons
                      style={{ layout: "vertical", shape: "pill", label: "pay" }}
                      createOrder={async (data, actions) => {
                        try {
                          // 1. Create a record on our server first to get a purchase_id
                          const res = await axios.post(`${API_URL}/api/trials/buy`, null, {
                            params: { admin_id: adminId, pack_id: pack.id }
                          });
                          
                          if (res.data.success) {
                            const internalPurchaseId = res.data.data.purchase_id;
                            
                            // 2. Return the PayPal order creation
                            return actions.order.create({
                              purchase_units: [
                                {
                                  amount: {
                                    currency_code: "USD",
                                    value: pack.price.toString(),
                                  },
                                  description: `${pack.name} - TCF Canada`,
                                  custom_id: internalPurchaseId // Link PayPal order to our DB record
                                },
                              ],
                            });
                          }
                        } catch (error) {
                          console.error("PayPal Init Error:", error);
                          toast.error("Erreur lors de l'initialisation du paiement");
                        }
                      }}
                      onApprove={async (data, actions) => {
                        try {
                          // Capture the order details
                          const order = await actions.order.capture();
                          const internalPurchaseId = order.purchase_units[0].custom_id;
                          
                          // Confirm on our backend
                          const confirmRes = await axios.post(`${API_URL}/api/trials/confirm`, null, {
                            params: {
                              purchase_id: internalPurchaseId,
                              payment_id: data.orderID
                            }
                          });
                          
                          if (confirmRes.data.success) {
                            toast.success("Paiement réussi ! Vos essais ont été ajoutés.");
                            loadData();
                          }
                        } catch (error) {
                          console.error("PayPal Approval Error:", error);
                          toast.error("Erreur lors de la validation du paiement");
                        }
                      }}
                      onError={(err) => {
                        console.error("PayPal Global Error:", err);
                        toast.error("Le service de paiement PayPal est temporairement indisponible.");
                      }}
                    />
                  ) : (
                    <button disabled className="w-full h-12 bg-muted text-muted-foreground rounded-2xl text-sm font-bold cursor-not-allowed">
                      Paiement indisponible
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Assign Pack Section */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Gift className="h-5 w-5 text-emerald-500" />
            </div>
            <span>Assigner un pack à un étudiant</span>
          </h3>

          {balance <= 0 ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-[2rem] p-6 flex items-start space-x-4">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-500 text-sm">Solde insuffisant</p>
                <p className="text-muted-foreground text-sm mt-1">
                  Vous ne disposez d'aucun essai IA. Achetez un pack dans la section <span className="font-semibold">« Acheter des Essais »</span> pour pouvoir distribuer à vos étudiants.
                </p>
              </div>
            </div>
          ) : centerPacks.length === 0 ? (
            <div className="bg-muted/30 border border-border rounded-[2rem] p-6 text-center text-muted-foreground text-sm">
              Aucun pack avec des essais IA configuré. Créez des packs dans l'onglet « Mes Packs ».
            </div>
          ) : (
            <form onSubmit={handleAssignPack} className="bg-card border border-border rounded-[2rem] p-8 space-y-6 shadow-sm">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Choisir le pack</label>
                <select
                  required
                  value={packAssignData.packId}
                  onChange={(e) => setPackAssignData({ ...packAssignData, packId: e.target.value })}
                  className="w-full h-14 px-5 rounded-2xl border bg-background/50 mt-1 focus:ring-2 focus:ring-primary outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">Sélectionner un pack...</option>
                  {centerPacks.map(p => (
                    <option key={p.id} value={p.id} disabled={(p.essais_ia || 0) > balance}>
                      {p.name} — {p.essais_ia} essais IA{(p.essais_ia || 0) > balance ? ' (solde insuffisant)' : ''}
                    </option>
                  ))}
                </select>
                {packAssignData.packId && (() => {
                  const p = centerPacks.find(pk => pk.id === packAssignData.packId);
                  return p ? (
                    <p className="text-xs text-muted-foreground ml-1">
                      Ce pack débite <span className="font-bold text-emerald-500">{p.essais_ia} essais</span> de votre solde. Il vous restera <span className="font-bold">{balance - (p.essais_ia || 0)}</span> essais.
                    </p>
                  ) : null;
                })()}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Choisir l'étudiant</label>
                <select
                  required
                  value={packAssignData.userId}
                  onChange={(e) => setPackAssignData({ ...packAssignData, userId: e.target.value })}
                  className="w-full h-14 px-5 rounded-2xl border bg-background/50 mt-1 focus:ring-2 focus:ring-primary outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">Sélectionner un étudiant...</option>
                  {centerUsers.filter(u => u.role === 'user' && !u.is_archived).map(u => (
                    <option key={u.id} value={u.id}>{u.nom || u.username} ({u.credits_ia} essais actuels)</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={isAssigningPack}
                className="w-full h-16 bg-emerald-600 text-white rounded-2xl font-black text-lg hover:opacity-90 transition-all flex items-center justify-center space-x-3 shadow-xl shadow-emerald-600/20 disabled:opacity-50 disabled:grayscale"
              >
                {isAssigningPack ? <Loader2 className="animate-spin h-5 w-5" /> : <Gift className="h-5 w-5" />}
                <span>Attribuer le pack</span>
              </button>
            </form>
          )}
        </div>

        {/* Distribute Section (manual amount) */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Send className="h-5 w-5 text-primary" />
            </div>
            <span>Distribuer manuellement</span>
          </h3>

          {balance <= 0 ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-[2rem] p-6 flex items-start space-x-4">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-500 text-sm">Solde insuffisant</p>
                <p className="text-muted-foreground text-sm mt-1">
                  Votre solde est à <span className="font-bold text-red-500">0 essai IA</span>. Rechargez votre compte en achetant un pack avant de distribuer.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleDistribute} className="bg-card border border-border rounded-[2rem] p-8 space-y-6 shadow-sm">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Choisir l'étudiant</label>
                <select
                  required
                  value={distributionData.studentId}
                  onChange={(e) => setDistributionData({...distributionData, studentId: e.target.value})}
                  className="w-full h-14 px-5 rounded-2xl border bg-background/50 mt-1 focus:ring-2 focus:ring-primary outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">Sélectionner un étudiant...</option>
                  {centerUsers.filter(u => u.role === 'user' && !u.is_archived).map(u => (
                    <option key={u.id} value={u.id}>{u.nom || u.username} ({u.credits_ia} essais actuels)</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">
                  Nombre d'essais à envoyer
                  <span className="ml-2 text-primary font-black">(max : {balance})</span>
                </label>
                <input
                  required
                  type="number"
                  min="1"
                  max={balance}
                  value={distributionData.amount}
                  onChange={(e) => setDistributionData({...distributionData, amount: parseInt(e.target.value)})}
                  className="w-full h-14 px-5 rounded-2xl border bg-background/50 mt-1 focus:ring-2 focus:ring-primary outline-none transition-all"
                />
                {distributionData.amount > 0 && (
                  <p className="text-xs text-muted-foreground ml-1">
                    Il vous restera <span className="font-bold">{Math.max(0, balance - distributionData.amount)}</span> essais après envoi.
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={isDistributing || distributionData.amount > balance}
                className="w-full h-16 bg-primary text-primary-foreground rounded-2xl font-black text-lg hover:opacity-90 transition-all flex items-center justify-center space-x-3 shadow-xl shadow-primary/20 disabled:opacity-50 disabled:grayscale"
              >
                {isDistributing ? <Loader2 className="animate-spin h-5 w-5" /> : <Send className="h-5 w-5" />}
                <span>Envoyer les essais</span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
